// components/PaperTemplateModal.jsx
import React, { useState, useEffect, useMemo, memo } from 'react';
import { 
  FileText, 
  Search, 
  AlertTriangle,
  X, 
  Loader2, 
  ArrowRight,
  GraduationCap,
  Sparkles,
  Bookmark,
  Award
} from 'lucide-react';
import api from '../../api';

// Memoized Card Sub-component
const TemplateCard = memo(({ template, isSelected, onSelect, isEndExam }) => {
  const templateTypeLabel = template.templateType === 'cie_only' ? 'CIE Only' : 
                           template.templateType === 'end_exam_only' ? 'End Exam Only' : 
                           'Both';
  const templateTypeColor = template.templateType === 'cie_only' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                            template.templateType === 'end_exam_only' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                            'bg-emerald-50 text-emerald-700 border-emerald-100';

  return (
    <div
      onClick={() => onSelect(template._id)}
      className={`w-full p-4 text-left rounded-xl border transition-all duration-200 active:scale-[0.99] select-none touch-manipulation will-change-transform ${
        isSelected
          ? 'border-indigo-600 bg-indigo-50/40 shadow-sm ring-1 ring-indigo-600'
          : 'border-slate-200 bg-white hover:border-slate-300 shadow-sm'
      }`}
    >
      <div className="flex gap-3">
        <div className="pt-0.5 shrink-0">
          <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${
            isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 bg-white'
          }`}>
            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1.5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-slate-800 text-sm tracking-tight break-words">
                  {template.name}
                </h4>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${templateTypeColor} border`}>
                  {templateTypeLabel}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                {isEndExam ? (
                  <Award className="w-3 h-3 text-purple-500 shrink-0" />
                ) : (
                  <Bookmark className="w-3 h-3 text-slate-400 shrink-0" />
                )}
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                  {template.sections?.length || 0} Blueprint Segments
                </span>
              </div>
            </div>
            <div className="shrink-0 mt-1 sm:mt-0">
              <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md border border-slate-200">
                {template.maxMarks} Marks
              </span>
            </div>
          </div>

          {template.sections && template.sections.length > 0 && (
            <div className="mt-3 pt-2.5 border-t border-slate-100/80">
              <div className="flex gap-1.5 flex-wrap">
                {template.sections.map((section) => (
                  <span 
                    key={section._id} 
                    className={`text-[11px] px-2 py-0.5 rounded font-medium border ${
                      isSelected 
                        ? 'bg-indigo-100/40 border-indigo-200/40 text-indigo-700' 
                        : 'bg-slate-50 border-slate-200/60 text-slate-600'
                    }`}
                  >
                    {section.name}: <span className="font-semibold">{section.maxMarks}M</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {template.enforceCOPOMapping && (
            <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-amber-800 font-medium bg-amber-50 border border-amber-100/70 px-2 py-0.5 rounded-md w-max max-w-full">
              <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />
              <span className="truncate">OBE Criteria: CO-PO Mapping Needed</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default function PaperTemplateModal({ open, onClose, context, onTemplateSelected, isEndExam = false }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const tId = context?.tenantId;
  const pTypeId = context?.programTypeId;
  const pId = context?.programId;

  // DEBUG: Log when modal opens and props received
  console.log(`[PaperTemplateModal] 🎯 MODAL OPENED - isEndExam: ${isEndExam}, context:`, { tId, pTypeId, pId });

  useEffect(() => {
    if (!open || !tId) return;
    
    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          tenantId: tId,
          programTypeId: pTypeId,
          programId: pId,
          status: 'active'
        };
        
        console.log(`[PaperTemplateModal] 📡 FETCHING ${isEndExam ? 'END EXAM' : 'CIE'} templates with params:`, params);
        
        const apiEndpoint = isEndExam 
          ? '/api/faculty/end-exam-paper-templates/eligible'
          : '/api/faculty/paper-templates/eligible';
        
        console.log(`[PaperTemplateModal] 🔗 API Endpoint: ${apiEndpoint}`);
        
        const response = await api.get(apiEndpoint, { params });
        console.log(`[PaperTemplateModal] ✅ API Response (${isEndExam ? 'END EXAM' : 'CIE'}):`, response.data);
        console.log(`[PaperTemplateModal] 📊 Templates count: ${response.data.templates?.length || 0}`);
        setTemplates(response.data.templates || []);
      } catch (err) {
        console.error(`[PaperTemplateModal] ❌ Failed to load ${isEndExam ? 'End Exam' : 'CIE'} templates:`, err);
        setError(err.response?.data?.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, [open, tId, pTypeId, pId, isEndExam]);

  const handleSelectAndAssign = () => {
    if (!selectedTemplateId) return;
    const selectedTemplate = templates.find(t => t._id === selectedTemplateId);
    if (!selectedTemplate) {
      setError('Selected template not found');
      return;
    }
    console.log(`[PaperTemplateModal] 🎯 Template selected: ${selectedTemplate.name} (${selectedTemplate.templateType})`);
    onTemplateSelected(selectedTemplate);
    onClose();
  };

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const cleanQuery = searchQuery.toLowerCase();
    return templates.filter(t => t.name?.toLowerCase().includes(cleanQuery));
  }, [searchQuery, templates]);

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-xl max-h-[92vh] sm:max-h-[85vh] flex flex-col border border-slate-100 overflow-hidden transform transition-transform duration-200 ease-out will-change-transform"
        onClick={e => e.stopPropagation()}
      >
        {/* Academic Header Bar */}
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isEndExam ? 'bg-purple-50 text-purple-600' : 'bg-indigo-50 text-indigo-600'}`}>
              {isEndExam ? <Award className="w-5 h-5" /> : <GraduationCap className="w-5 h-5" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-800 tracking-tight truncate">
                Select {isEndExam ? 'End Exam' : 'Assessment'} Blueprint
              </h2>
              {context?.subjectCode && (
                <p className="text-[11px] text-slate-400 font-medium font-mono truncate">
                  Course Mapping &bull; {context.subjectCode}
                </p>
              )}
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition touch-manipulation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Input Bar Panel */}
        {!loading && !error && templates.length > 0 && (
          <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2 shrink-0">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input 
              type="text"
              placeholder="Search template patterns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent border-none text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-0 p-0"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="text-[11px] text-slate-400 hover:text-indigo-600 font-medium px-1 touch-manipulation"
              >
                Reset
              </button>
            )}
          </div>
        )}
        
        {/* Main Content Pane */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/40 space-y-3 scroll-smooth scrolling-touch">
          {loading && (
            <div className="text-center py-12 flex flex-col items-center justify-center">
              <Loader2 className="w-7 h-7 text-indigo-600 animate-spin mb-2" />
              <p className="text-xs font-medium text-slate-500">Syncing curriculum templates...</p>
            </div>
          )}
          
          {error && (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3.5 text-rose-800 flex items-start gap-2.5 text-xs shadow-sm">
              <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold block mb-0.5">Database Sync Failed</span>
                <p className="text-rose-700/90">{error}</p>
              </div>
            </div>
          )}
          
          {!loading && !error && templates.length === 0 && (
            <div className="text-center py-12 max-w-xs mx-auto">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 text-slate-400 border border-slate-200/60 shadow-sm ${isEndExam ? 'bg-purple-50' : 'bg-slate-100'}`}>
                {isEndExam ? <Award className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
              </div>
              <h3 className="text-sm font-bold text-slate-800 tracking-tight">No {isEndExam ? 'End Exam' : 'Assessment'} Templates</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-[240px] mx-auto leading-normal">
                There are currently no active {isEndExam ? 'end exam' : 'assessment'} question paper patterns assigned.
              </p>
              <div className="mt-4 px-3 py-2 bg-slate-100 rounded-lg inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 border border-slate-200">
                <span>Please contact the Controller of Examinations</span>
              </div>
            </div>
          )}

          {!loading && !error && templates.length > 0 && filteredTemplates.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-xs font-medium">
              No matching records for "{searchQuery}"
            </div>
          )}
          
          {/* Template Card Mapping Section */}
          {!loading && !error && filteredTemplates.map(template => (
            <TemplateCard
              key={template._id}
              template={template}
              isSelected={selectedTemplateId === template._id}
              onSelect={setSelectedTemplateId}
              isEndExam={isEndExam}
            />
          ))}
        </div>
        
        {/* Footnotes Action Bar Container */}
        <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-between shrink-0 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.02)] safe-bottom">
          <button 
            onClick={onClose} 
            className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition active:scale-[0.98] touch-manipulation"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSelectAndAssign}
            disabled={!selectedTemplateId}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm flex items-center gap-1.5 active:scale-[0.98] touch-manipulation"
          >
            <span>Continue</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}